// @ts-nocheck
/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { Alert } from '@mui/material';

import { ReactComponent as CheckIcon } from '../assets/CheckIcon.svg';
import { ReactComponent as InfoIcon } from '../assets/InfoIcon.svg';
import { ReactComponent as ErrorIcon } from '../assets/ErrorIcon.svg';
import { ReactComponent as WarningIcon } from '../assets/WarningTriangleIcon.svg';
import { ReactComponent as CircleCheckIcon } from '../assets/CircleCheckIcon.svg';
import SolyticsBox from './SolyticsBox';
import SolyticsTypography from './SolyticsTypography';

// icon map
const ICON_MAP = {
  success: { text: CheckIcon, default: CircleCheckIcon },
  info: { text: InfoIcon, default: InfoIcon },
  error: { text: ErrorIcon, default: ErrorIcon },
  warning: { text: WarningIcon, default: WarningIcon },
};

const ICON_COLOR = (variant, color, theme) => {
  if (variant === 'text' && color === 'success') {
    return theme.palette.skyBlue[50];
  }
  if (variant === 'filled') {
    return theme.palette[color].contrastText;
  }

  return 'inherit';
};

// Common base styles for alert container
const BASE_ALERT_STYLES = (theme) => ({
  width: '100%',
  display: 'flex',
  alignItems: 'flex-start',
  fontFamily: theme.typography.fontFamily,
  boxSizing: 'border-box',
});

const ALERT_TYPE_STYLES = {
  alertMessage: (theme) => ({
    fontSize: theme.typography.caption.fontSize,
    lineHeight: theme.typography.caption.lineHeight,
    borderRadius: '4px',
    padding: '8px 16px',
    gap: '8px',
  }),
  snackbarMessage: (theme) => ({
    minHeight: '54px',
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight,
    borderRadius: '6px',
    gap: '8px',
  }),
};

// Common icon box styles
const BASE_ICON_BOX_STYLES = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

// Common variant-specific style helpers
const filledVariant = (theme, color) => ({
  backgroundColor: theme.palette[color].main,
  color: theme.palette[color].contrastText,
  fontWeight: 500,
});

const lightVariant = (theme, color, type) => {
  if (color === 'success') {
    return {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.grey[60]}`,
      fontWeight: 400,
    };
  }
  if (color === 'error') {
    return {
      backgroundColor: theme.palette.error[20],
      color: theme.palette.error[70],
      border: `1px solid ${theme.palette.grey[60]}`,
      fontWeight: 400,
    };
  }
  if (color === 'warning') {
    return {
      backgroundColor: theme.palette.warning[20],
      color: theme.palette.warning[80],
      border: `1px solid ${theme.palette.grey[60]}`,
      fontWeight: 400,
    };
  }
  if (color === 'info') {
    if (theme.palette.mode === 'dark') {
      return {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.info.main}`,
        fontWeight: 400,
      };
    }

    // Use different blue for alertMessage vs snackbarMessage
    return {
      backgroundColor:
        type === 'alertMessage'
          ? theme.palette.blue[10]
          : theme.palette.lavenderBlue[10],
      color: theme.palette.text.primary,
      border:
        type === 'alertMessage'
          ? `1px solid ${theme.palette.skyBlue[30]}`
          : `1px solid ${theme.palette.lavenderBlue[30]}`,
      fontWeight: 400,
    };
  }
  return {};
};

const textVariant = (theme, color) => {
  if (color === 'success') {
    return {
      backgroundColor: 'transparent',
      color: theme.palette.skyBlue[50],
      border: `1px solid ${theme.palette.grey[60]}`,
      fontWeight: 500,
    };
  }
  return {
    backgroundColor: 'transparent',
    color: theme.palette[color].main,
    fontWeight: 400,
  };
};

const VARIANT_MAP = {
  alertMessage: {
    filled: (theme, color) => filledVariant(theme, color),
    light: (theme, color) => lightVariant(theme, color, 'alertMessage'),
    text: (theme, color) => textVariant(theme, color),
  },
  snackbarMessage: {
    filled: (theme, color) => filledVariant(theme, color),
    light: (theme, color) => lightVariant(theme, color, 'snackbarMessage'),
    text: (theme, color) => textVariant(theme, color),
  },
};

const getAriaProps = (color) => ({
  role: color === 'error' || color === 'warning' ? 'alert' : 'status',
  'aria-live':
    color === 'error' || color === 'warning' ? 'assertive' : 'polite',
});

const SolyticsAlert = React.forwardRef(
  (
    {
      message,
      variant = 'light',
      color = 'success',
      icon = true,
      type = 'alertMessage', // 'alertMessage' | 'snackbarMessage'
      noWrap = false,
    },
    ref
  ) => {
    // Unified icon logic
    const Icon = ICON_MAP[color]?.[variant === 'text' ? 'text' : 'default'];
    const iconBoxSx = useMemo(
      () => (theme) => ({
        ...BASE_ICON_BOX_STYLES,
        height: type === 'snackbarMessage' ? 24 : 16,
        width: type === 'snackbarMessage' ? 24 : 16,
        color: ICON_COLOR(variant, color, theme),
      }),
      [type, variant, color]
    );

    const iconElement = useMemo(() => {
      if (!icon || !Icon) return null;
      return (
        <SolyticsBox sx={iconBoxSx}>
          <Icon width={16} height={16} />
        </SolyticsBox>
      );
    }, [icon, Icon, iconBoxSx]);

    // Style logic
    const alertSx = useMemo(
      () => (theme) => ({
        ...BASE_ALERT_STYLES(theme),
        ...ALERT_TYPE_STYLES[type](theme),
        ...VARIANT_MAP[type][variant](theme, color),
        '& .MuiAlert-message': {
          padding: 0,
          minWidth: 0,
          flex: 1,
          whiteSpace: noWrap ? 'nowrap' : 'normal',
          overflow: noWrap ? 'hidden' : 'visible',
          textOverflow: noWrap ? 'ellipsis' : 'clip',
          overflowWrap: noWrap ? 'normal' : 'anywhere',
          wordBreak: noWrap ? 'normal' : 'break-word',
        },
        '& .MuiAlert-icon': { padding: 0, marginRight: 0 },
      }),
      [type, variant, color, noWrap]
    );

    const ariaProps = useMemo(() => getAriaProps(color), [color]);

    return (
      <Alert
        {...ariaProps}
        ref={ref}
        aria-atomic="true"
        tabIndex={0}
        icon={iconElement}
        severity={color}
        sx={alertSx}
      >
        <SolyticsTypography
          variant={type === 'alertMessage' ? 'caption' : 'body2'}
          noWrap={noWrap}
          sx={{
            color: 'inherit',
            whiteSpace: noWrap ? 'nowrap' : 'normal',
            overflow: noWrap ? 'hidden' : 'visible',
            textOverflow: noWrap ? 'ellipsis' : 'clip',
            overflowWrap: noWrap ? 'normal' : 'anywhere',
            wordBreak: noWrap ? 'normal' : 'break-word',
          }}
        >
          {message}
        </SolyticsTypography>
      </Alert>
    );
  }
);

SolyticsAlert.displayName = 'SolyticsAlert';

export default SolyticsAlert;
